import { FC, useEffect, useState } from "react";
import { PlayerMatchListDto } from "../../../types/stats";
import { getPlayerMatchStatsById } from "../../../api/transport/statsTransport";
import { TeamContext } from "../../../types/common";
import { PlayerDto, StatParams } from "../../../types/player";
import { Header, Title } from "./styled";
import { StatParamsRadioGroup } from "./StatParamsRadioGroup";
import { getHashParam, getStaticUrl, getTeamIdByCode, getTeamNameByCode, updateHashParams } from "../../../utils";

interface PlayerMatchesWidgetProps {
    teamContext?: TeamContext;
    tournamentUuid?: string;
    seasonUuid?: string;
    teamId?: number;
    playerId: string;
    playerData?: PlayerDto;
    filters?: StatParams[];
    defaultFilter?: StatParams;
}

export const PlayerMatchesWidget: FC<PlayerMatchesWidgetProps> = ({
    playerId,
    playerData,
    teamContext,
    tournamentUuid,
    teamId,
    seasonUuid,
    filters = [],
    defaultFilter = StatParams.MATCHES,
}) => {
    const [playerMatchStats, setPlayerMatchStats] = useState<PlayerMatchListDto>([]);
    const hashFilter = getHashParam('filter') as StatParams | null;
    const [selectedFilter, setSelectedFilter] = useState<StatParams>(hashFilter || defaultFilter);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!playerId || !playerData) {
            return;
        }

        if (filters?.length) {
            if (!filters.includes(selectedFilter)) {
                setSelectedFilter(filters[0]);
                return;
            }
        }

        setLoading(true);

        getPlayerMatchStatsById(playerId, tournamentUuid, seasonUuid, teamId, teamContext, selectedFilter).then(
            (data) => {
                setPlayerMatchStats(data.sort((a, b) => new Date(a.date) > new Date(b.date) ? -1 : 1));
            }
        ).finally(() => {
            setLoading(false);
        });
    }, [filters, playerData, playerId, seasonUuid, selectedFilter, teamContext, teamId, tournamentUuid]);

    if (!filters?.length && !playerMatchStats?.length) {
        return null;
    }

    let title = 'Сухие матчи:';
    let paramName = '';
    let paramKey = 'minutesPlayed';

    if (selectedFilter === StatParams.GOALS) {
        title = 'Забивал в матчах:';
        paramName = 'Голы';
        paramKey = 'goals';
    } else if (selectedFilter === StatParams.ASSISTS) {
        title = 'Ассистировал в матчах:';
        paramName = 'Ассисты';
        paramKey = 'assists';
    } else if (selectedFilter === StatParams.YELLOW_CARDS) {
        title = 'Получал желтые карточки:';
        paramName = 'ЖК';
        paramKey = 'yellowCards';
    } else if (selectedFilter === StatParams.RED_CARDS) {
        title = 'Получал красные карточки:';
        paramName = '';
        paramKey = 'redCards';
    } else if (selectedFilter === StatParams.MATCHES || selectedFilter === StatParams.MINUTES_PLAYED) {
        title = 'Выходил на поле в матчах:';
        paramName = 'Минут';
        paramKey = 'minutesPlayed';
    } else if (selectedFilter === StatParams.PENALTIES_SAVED) {
        title = 'Отбивал пенальти:';
        paramName = 'Отбито';
        paramKey = 'penaltiesSaved';
    } else if (selectedFilter === StatParams.CLEAN_SHEETS) {
        paramName = 'Минут';
        paramKey = 'minutesPlayed';
    }

    return (
        <div className="flex flex-col items-center w-full box-border px-2">
            {filters?.length <= 1 && <Title>
                {title}
            </Title>}
            {filters?.length > 1 && (
                <Header>
                    <Title>
                        Матчи
                    </Title>
                    <StatParamsRadioGroup
                        value={selectedFilter}
                        onChange={(value: StatParams) => {
                            setSelectedFilter(value);
                            updateHashParams({ filter: value });
                        }}
                        params={filters}
                    />
                    <div />
                </Header>
            )}
            <div className="w-full flex flex-col gap-2 sm:gap-3" >
                {playerMatchStats.map(s => {
                    let homeTeam = {
                        logo: '',
                        name: '',
                    }

                    let guestTeam = {
                        logo: '',
                        name: '',
                    }

                    if (s.isHome) {
                        homeTeam = {
                            logo: 'https://fcrodina.com/i/logo.png',
                            name: getTeamNameByCode(teamContext),
                        }
                        guestTeam = {
                            logo: s.opponentTeamLogo,
                            name: s.opponentTeamName,
                        }
                    } else {
                        guestTeam = {
                            logo: 'https://fcrodina.com/i/logo.png',
                            name: getTeamNameByCode(teamContext),
                        }
                        homeTeam = {
                            logo: s.opponentTeamLogo,
                            name: s.opponentTeamName,
                        }
                    }

                    const paramValue = s?.[paramKey];

                    return (
                        <div key={s.matchId} className="w-full mx-auto max-w-[500px]">
                            <div className="rounded-xl border-slate-100 px-4 text-lg text-slate-500">
                                {new Date(s.date).toLocaleDateString('ru-RU')}
                            </div>
                            <div className="w-full text-xl sm:text-2xl gap-4 border border-solid border-slate-100 rounded-2xl bg-slate-100 text-slate-950 flex flex-row justify-between">
                                <div className="w-full flex flex-col gap-3 p-4 sm:p-6">
                                    <div className="flex flex-row justify-between items-center">
                                        <div className="flex flex-row justify-start items-center gap-4">
                                            <img src={getStaticUrl(homeTeam.logo)} className="h-10 sm:h-16 aspect-square object-contain" />
                                            {homeTeam.name}
                                        </div>
                                        <div className="text-2xl sm:text-3xl font-medium">
                                            {s.score.split(':')[0]}
                                        </div>
                                    </div>
                                    <div className="flex flex-row justify-between items-center">
                                        <div className="flex flex-row justify-start items-center gap-4">
                                            <img src={getStaticUrl(guestTeam.logo)} className="h-10 sm:h-16 aspect-square object-contain" />
                                            {guestTeam.name}
                                        </div>
                                        <div className="text-2xl sm:text-3xl font-medium">
                                            {s.score.split(':')[1]}
                                        </div>
                                    </div>
                                </div>
                                {!!paramName && !!paramValue && <div className="flex flex-col items-center justify-center min-w-20 p-4 sm:p-6 sm:gap-4 border-0 border-l border-solid border-l-slate-200">
                                    <div>
                                        {paramName}
                                    </div>
                                    <div className="font-bold">
                                        {paramValue}
                                    </div>
                                </div>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
